import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { updatePrioridad } from "../Api/prioridad";
import { RootState } from "../app/store";
import { FechasPrioridad } from "../types";
import { StudentState } from "../reduxSlices/studentSlice";
import { calcularOffsetRelojServidor } from "../utils/prioridadClock";

export interface PrioridadResponse {
  tienePrioridad: boolean;
  mensajePrioridad: string;
  fechasPrioridad: FechasPrioridad;
  debeMostrarContador: boolean;
  debeConsultarCupos: boolean;
  recibidoEnMs: number;
}

// Variable para rastrear si hay un request en curso
let pendingRequest: Promise<PrioridadResponse> | null = null;

// Constantes para reintentos
const MAX_RETRIES = 4;
const RETRY_DELAY_MS = 2000; // 2 segundos

// Función helper para esperar
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const updatePrioridadThunk = createAsyncThunk<
  PrioridadResponse,
  boolean | null,
  { state: RootState }
>(
  "prioridad/updatePrioridad",
  async (prioridad: boolean | null, thunkAPI) => {
    try {
      // Si ya hay un request en curso, esperar a que termine y retornar el mismo resultado
      if (pendingRequest) {
        console.log("🔄 Request de prioridad ya en curso, reutilizando...");
        return await pendingRequest;
      }

      // Verificar que el token esté disponible
      const state = thunkAPI.getState();
      const token = state.student.token;
      const storedToken = localStorage.getItem("token");
      
      if (!token && !storedToken) {
        console.warn("⚠️ No hay token disponible para updatePrioridad");
        return thunkAPI.rejectWithValue("Token no disponible");
      }
      
      if (token === "x" || storedToken === "x") {
        console.warn("⚠️ Token inválido para updatePrioridad");
        return thunkAPI.rejectWithValue("Token inválido");
      }
      
      // Crear el request con lógica de reintentos
      pendingRequest = (async () => {
        let lastError: any = null;
        
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          try {
            console.log(`🔄 Intento ${attempt} de ${MAX_RETRIES} para actualizar prioridad...`);
            const response = await updatePrioridad(prioridad);
            console.log(`✅ Prioridad actualizada exitosamente en intento ${attempt}`);
            return response;
          } catch (error: any) {
            lastError = error;
            console.error(`❌ Error en intento ${attempt} de ${MAX_RETRIES}:`, error);
            
            // Si no es el último intento, esperar antes de reintentar
            if (attempt < MAX_RETRIES) {
              console.log(`⏳ Esperando ${RETRY_DELAY_MS}ms antes del siguiente reintento...`);
              await delay(RETRY_DELAY_MS);
            }
          }
        }
        
        // Si llegamos aquí, se agotaron todos los reintentos
        console.error(`❌ Se agotaron los ${MAX_RETRIES} intentos para actualizar prioridad`);
        throw new Error("REINTENTOS_AGOTADOS");
      })();
      
      try {
        const response = await pendingRequest;
        return response;
      } finally {
        // Limpiar el request pendiente después de completarse
        pendingRequest = null;
      }
    } catch (error: any) {
      // Limpiar el request pendiente en caso de error
      pendingRequest = null;
      
      // Si el error es de reintentos agotados, lanzar un error especial
      if (error.message === "REINTENTOS_AGOTADOS") {
        return thunkAPI.rejectWithValue("REINTENTOS_AGOTADOS");
      }
      
      console.error("❌ Error en updatePrioridadThunk:", error);
      return thunkAPI.rejectWithValue(error.message || "Error al actualizar prioridad");
    }
  }
);

export const updatePrioridadBuilder = (builder: ActionReducerMapBuilder<StudentState>) => {
  builder
    .addCase(updatePrioridadThunk.pending, (state) => {
      // Opcional: agregar estado de loading si es necesario
      console.log("🔄 Actualizando prioridad...");
    })
    .addCase(updatePrioridadThunk.fulfilled, (state, action) => {
      const {
        tienePrioridad,
        mensajePrioridad,
        fechasPrioridad,
        debeMostrarContador,
        debeConsultarCupos,
        recibidoEnMs,
      } = action.payload;

      state.prioridad = tienePrioridad;
      state.mensajePrioridad = mensajePrioridad;
      state.fechasPrioridad = fechasPrioridad;
      state.mostrarCountdownPrioridad = debeMostrarContador;
      state.debeConsultarCupos = !!debeConsultarCupos;

      // Se calibra solo cuando la respuesta trae hora de servidor; si no, se conserva el offset anterior.
      const offset = calcularOffsetRelojServidor(fechasPrioridad?.fechaInicioContador, recibidoEnMs);
      if (offset !== null) {
        state.serverClockOffsetMs = offset;
      }

      console.log("✅ Prioridad actualizada exitosamente");
    })
    .addCase(updatePrioridadThunk.rejected, (state, action) => {
      console.error("❌ Error al actualizar prioridad:", action.payload);
      
      // Si el error es por token no disponible, no cambiar el estado
      if (action.payload === "Token no disponible" || action.payload === "Token inválido") {
        console.warn("⚠️ Omitiendo actualización de prioridad por problema de token");
        return;
      }
      
      // Para otros errores, podrías actualizar el mensaje de error
      // state.mensajePrioridad = "Error al consultar prioridad";
    });
};
